import Emitter from '../Utils/Emitter';
import { SegmentEvent } from '../Utils/Constants';
import Utils from '../Utils/Utils';
import HlsParser from '../Utils/HlsParser';
import DashParser from '../Utils/DashParser';
import Logger from '../Utils/Logger';
import p2pManager from '../p2p/P2PManager';
/**
 * @class
 * @description Video segment class
 * It contains the url of the request, with host that can be modified to request it to a different cdn.
 * Also has unique id for every segment, and when requested, the response data and the size of it.
 * Additionally when requested to a CDN, it stores the bandwidth calculated on the request
 * and when was it used on cache for last time.
 * @exports VideoSegment
 * @extends Emitter
 */
export class VideoSegment extends Emitter {
    /**
     * Constructs VideoSegment.
     * @param {string} id id code of the segment.
     * @param {string} url initial full url of the video segment.
     * @param {Object} headers optional object with headers for the requests.
     * @param {number} retries number of retries before discarding the request of the segment, 3 by default.
     * @param {boolean} p2p boolean that indicates if the following request has to be done via p2p mechanism.
     * @param {boolean} arrayBuffer set as true if is expected to get the response in ArrayBuffer format.
     */
    constructor(pathName, url, resource, headers = {}, retries = 3, p2p = false, arrayBuffer = false) {
        var _a;
        super();
        this.pathName = "";
        this.data = new ArrayBuffer(1);
        this.size = 0;
        this.bandwidth = 0;
        this.startTime = 0;
        this.endTime = 0;
        this.downloadTimeMs = 0;
        this.failed = false;
        this.progressDownloadedBytes = 0;
        this.progressDownloadedBytesLastUpdate = 0;
        this.instantBandwidth = 0;
        this.duration = 0;
        this.initialEstimatedTime = 0;
        this.lastEstimatedTime = 0;
        this.isManifest = false;
        this.isMedia = false;
        this.download = true; // To prevent using internal download
        this.realBodySize = 0;
        this.readyForP2P = true;
        this.announcedP2P = false;
        this.activeP2PSendProcesses = 0;
        this.retryBecauseNewHeaders = 0;
        this.forceHttp = false;
        const urlObj = new URL(url);
        this.url = url;
        this.finalUrl = url;
        this.origRes = url;
        this.host = urlObj.host;
        this.route = urlObj.pathname;
        this.params = urlObj.search;
        this.headers = headers;
        this.retries = retries;
        this.useP2P = p2p;
        this.triedP2P = false;
        this.cdnName = '';
        this.cdnProvider = '';
        this.arrayBuffer = arrayBuffer;
        this.triedCdns = [];
        this._resource = resource;
        this.pathName = pathName;
        this.id = Math.floor(Math.random() * 100) + "-" + Date.now();
        const header = ((_a = this.headers) === null || _a === void 0 ? void 0 : _a.Range) || '';
        this.p2pId = p2pManager.Util.hash(this.pathName + header);
    }
    updateP2PId() {
        var _a;
        const header = ((_a = this.headers) === null || _a === void 0 ? void 0 : _a.Range) || '';
        this.p2pId = p2pManager.Util.hash(this.pathName + header);
    }
    getNewUrl() {
        let override = false;
        if (!this.isManifest)
            override = true;
        else if (this.isManifest && this.failed)
            override = true;
        let newUrl = override ? this.finalUrl : this.url;
        if (this.params) {
            if (newUrl.includes("?")) {
                newUrl = newUrl + "&" + this.params;
            }
            else {
                newUrl = newUrl + "?" + this.params;
            }
        }
        return newUrl;
    }
    /**
     * Changes the target URL.
     * @param {string} newUrl New URL of the selected cdn we want to use.
     * @param {string} cdnName name of the new target CDN.
     * @public
     */
    changeCDN(newUrl, cdnName, cdnProvider) {
        if (cdnProvider != 'ORIGINAL') {
            const urlObj = new URL(newUrl);
            this.host = urlObj.host;
            this.route = urlObj.pathname;
            this.triedCdns.push(cdnName);
        }
        this.cdnName = cdnName;
        this.cdnProvider = cdnProvider;
    }
    /**
     * Requests this video segment with the url it has (host, route, params).
     * Throws success event when done.
     * @param {boolean} secure Optional, if should be forced to use https.
     * @public
     */
    /* public requestToCdn( secure?: boolean): void {
   
       if( !this.download )
         return;
    
       this.startTime = Date.now();
       let isManifest = false;
    
       ManifestExtensions.forEach((ext) => {
         isManifest = isManifest || this.route.indexOf(ext) >= 0;
       });
    
   
   
   
       // Create object to download data
       const xhr = new XhrRequest(this.host, this.route, isManifest && !this.arrayBuffer ? undefined : 'arraybuffer');
    
       // Process on success
       xhr.on(Events.Get.Success, () => {
           
           let result = this.onSuccess( xhr.getXHR().response );
   
           if( result ){
             this.emit(SegmentEvent.Success, {
               segment: this,
               data: this.data,
               size: this.size,
               progressDownloadedBytes: this.progressDownloadedBytes
             });
           }else{
             this.bandwidth = 0;
             this.emit(SegmentEvent.Fail, {
               segment: this,
               host: this.host,
               cdnName: this.cdnName
             });
           }
   
       });
   
     
       xhr.getXHR().onprogress = (event) => {
           this.onProgress( event );
   
           this.emit(SegmentEvent.Progress, {
             segment: this,
             host: this.host,
             cdnName: this.cdnName
           });
       }
       
       this.progressDownloadedBytesLastUpdate = this.startTime;
       xhr.send(this.params, this.headers, undefined, secure );
     }*/
    /**
     * Function to be called to set the data to this video segment externally.
     * For example when obtained the data by using P2P.
     * @param {ArrayBuffer} data The data object.
     * @public
     */
    setContent(data) {
        this.data = data;
        this.size = data.byteLength;
        this.emit(SegmentEvent.Success, {
            segment: this,
            data: data
        });
    }
    /**
     * Function to be called when an attempt to get the data externally, like using P2P, failed.
     * @public
     */
    noContentObtained() {
        this.emit(SegmentEvent.Fail, {
            segment: this
        });
    }
    getPerfLoadTime(segmentRoute) {
        var _a;
        if (typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function') {
            const resources = performance.getEntriesByType('resource');
            if (resources && resources.length > 0) {
                for (const resource of resources) {
                    const jsonResource = resource.toJSON();
                    if ((_a = jsonResource === null || jsonResource === void 0 ? void 0 : jsonResource.name) === null || _a === void 0 ? void 0 : _a.includes(segmentRoute)) {
                        return Math.round(jsonResource === null || jsonResource === void 0 ? void 0 : jsonResource.duration);
                    }
                }
            }
        }
        return -1;
    }
    onProgress(xhr, event) {
        console.log("P2P5 on progress" + event.total);
        if (event.total > 0)
            this.realBodySize = event.total;
        if (this.progressDownloadedBytesLastUpdate == 0) {
            this.progressDownloadedBytesLastUpdate = this.startTime;
        }
        let now = Date.now();
        let lapse = now - this.progressDownloadedBytesLastUpdate;
        this.endTime = Date.now();
        // to avoid a exces of data
        if (lapse < 150)
            return;
        if (lapse != 0) {
            this.instantBandwidth = ((event.loaded - this.progressDownloadedBytes) / ((now - this.progressDownloadedBytesLastUpdate) / 1000)) * 8;
            this.progressDownloadedBytesLastUpdate = now;
            if (event.loaded)
                this.progressDownloadedBytes = event.loaded;
        }
        let estimatedTimeDownloadTime = (now - this.startTime) / (event.loaded / event.total);
        this.initialEstimatedTime = this.initialEstimatedTime == 0 ? estimatedTimeDownloadTime : this.initialEstimatedTime;
        this.lastEstimatedTime = estimatedTimeDownloadTime;
        if (this.duration > 0 && estimatedTimeDownloadTime != 0) {
            if ((this.duration * 1000) < estimatedTimeDownloadTime) {
                Logger.log("High risk of buffer.");
            }
        }
    }
    onSuccess(response) {
        this.endTime = Date.now();
        if (response == undefined || response.length == 0) {
            return false;
        }
        this.downloadTimeMs = this.endTime - this.startTime;
        const perfTime = this.getPerfLoadTime(this.route);
        let pathname = new URL(this.url).pathname;
        if (pathname.endsWith('.m3u8')) {
            response = Utils.ifByteArrayToString(response);
            HlsParser.parse(this.url, response, this._resource);
            Logger.log("Parsed HLS manifest -> " + this.url + " renditions -> " + this._resource.getRenditions().size);
        }
        if (pathname.endsWith('.mpd')) {
            response = Utils.ifByteArrayToString(response);
            DashParser.parse(this.url, response, this._resource);
            Logger.log("Parsed DASH manifest -> " + this.url + " renditions -> " + this._resource.getRenditions().size);
        }
        Logger.log("BYTLELENGHT " + response.byteLength);
        if (response.byteLength != 0) {
            try {
                this.data = response.slice(0, response.byteLength);
                this.size = this.downloadTimeMs > 40 ? this.data.byteLength : 0;
                this.cachedSize = this.downloadTimeMs < 40 ? 0 : this.data.byteLength;
                this.bandwidth = this.downloadTimeMs > 40 ? this.data.byteLength / (this.downloadTimeMs / 1000) : 0;
                if (this.data.byteLength > 0)
                    this.realBodySize = this.data.byteLength;
                this.instantBandwidth = 0;
                this.progressDownloadedBytes = this.size;
            }
            catch (e) {
                Logger.log("Problem slicing the array");
            }
        }
        else {
            if (this.data.byteLength != this.realBodySize)
                this.data = new ArrayBuffer(1);
        }
        this.progressDownloadedBytesLastUpdate = this.endTime;
        return true;
    }
}
